import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Plus, Percent, Trash2, Edit, RefreshCw, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useCompanyScope } from '../context/CompanyScopeContext';

export default function LegalDeductions() {
  const { selectedCompanyId } = useCompanyScope();
  const [deductions, setDeductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: '', percentage: '', description: '' });

  // Déclaré AVANT useEffect pour éviter tout problème de référence
  const fetchDeductions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = selectedCompanyId ? { company_id: selectedCompanyId } : {};
      const res = await api.get('/legal-deductions', { params });
      setDeductions(res.data || []);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Erreur de chargement';
      setError(msg);
      console.error('[LegalDeductions] GET error:', e);
    } finally {
      setLoading(false);
    }
  }, [selectedCompanyId]);

  useEffect(() => {
    fetchDeductions();
  }, [fetchDeductions]); // Se relance aussi si selectedCompanyId change

  const resetForm = () => {
    setForm({ name: '', percentage: '', description: '' });
    setEditId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, percentage: parseFloat(form.percentage) };
      const params = selectedCompanyId ? { company_id: selectedCompanyId } : {};
      if (editId) {
        await api.put(`/legal-deductions/${editId}`, payload);
        toast.success('Retenue modifiée');
      } else {
        await api.post('/legal-deductions', payload, { params });
        toast.success('Retenue créée');
      }
      setOpen(false);
      resetForm();
      await fetchDeductions(); // Recharge explicitement après création
    } catch (err) {
      console.error('[LegalDeductions] Submit error:', err);
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (d) => {
    setEditId(d.id);
    setForm({ name: d.name, percentage: d.percentage.toString(), description: d.description || '' });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette retenue ?')) return;
    try {
      await api.delete(`/legal-deductions/${id}`);
      toast.success('Retenue supprimée');
      await fetchDeductions();
    } catch (e) {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6" data-testid="legal-deductions-page">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-3xl text-slate-900">Retenues Légales</h1>
          <p className="text-slate-600 mt-1">CNSS, IPR, ONEM et autres retenues obligatoires</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDeductions} disabled={loading} title="Rafraîchir">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => { resetForm(); setOpen(true); }} data-testid="add-deduction-btn">
            <Plus className="w-4 h-4 mr-2" /> Ajouter
          </Button>
        </div>
      </div>

      {/* Erreur */}
      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span>{error}</span>
          <Button size="sm" variant="ghost" onClick={fetchDeductions} className="ml-auto text-red-700">
            Réessayer
          </Button>
        </div>
      )}

      {/* Chargement */}
      {loading && (
        <div className="text-center py-12 text-slate-500">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
          Chargement...
        </div>
      )}

      {/* Liste vide */}
      {!loading && !error && deductions.length === 0 && (
        <div className="text-center py-12 text-slate-500 border-2 border-dashed border-slate-200 rounded-lg">
          <Percent className="w-8 h-8 mx-auto mb-2 text-slate-300" />
          <p>Aucune retenue légale configurée.</p>
          <Button className="mt-3" onClick={() => { resetForm(); setOpen(true); }}>
            <Plus className="w-4 h-4 mr-2" /> Ajouter la première retenue
          </Button>
        </div>
      )}

      {/* Grille des retenues */}
      {!loading && deductions.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {deductions.map(d => (
            <Card
              key={d.id}
              className={`border ${d.is_default ? 'border-blue-300 bg-blue-50/30' : 'border-slate-200'}`}
              data-testid={`deduction-${d.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Percent className="w-4 h-4 text-blue-600" />
                    <span className="font-semibold">{d.name}</span>
                  </div>
                  {d.is_default && (
                    <Badge variant="outline" className="text-xs bg-blue-100 text-blue-700">
                      Par défaut
                    </Badge>
                  )}
                </div>
                <p className="text-2xl font-bold text-blue-700">{d.percentage}%</p>
                {d.description && <p className="text-sm text-slate-500 mt-1">{d.description}</p>}
                <div className="flex gap-2 mt-3">
                  <Button size="sm" variant="outline" onClick={() => handleEdit(d)}>
                    <Edit className="w-3 h-3 mr-1" /> Modifier
                  </Button>
                  {!d.is_default && (
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(d.id)}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialogue Ajouter / Modifier */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editId ? 'Modifier' : 'Ajouter'} une retenue légale</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nom *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="ex: CNSS, IPR, INPP..."
                required
                data-testid="deduction-name"
              />
            </div>
            <div className="space-y-2">
              <Label>Pourcentage (%) *</Label>
              <Input
                type="number"
                step="0.001"
                min="0"
                max="100"
                value={form.percentage}
                onChange={(e) => setForm({ ...form, percentage: e.target.value })}
                placeholder="ex: 5.5"
                required
                data-testid="deduction-pct"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Explication de cette retenue"
                data-testid="deduction-desc"
              />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="flex-1">
                Annuler
              </Button>
              <Button type="submit" className="flex-1" disabled={submitting} data-testid="deduction-submit">
                {submitting
                  ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Enregistrement...</>
                  : editId ? 'Modifier' : 'Ajouter'
                }
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
